import { DateRange } from 'src/app/common/utils/helpers';
import { SeasonEnum } from 'src/types/graphql';

const today = new Date();
/**
 * Checks if two given dates are on the same day.
 * @param first - The first date to compare.
 * @param second - The second date to compare.
 * @returns Returns true if the given dates are on the same day, otherwise false.
 */
export const datesAreOnSameDay = (first: Date, second: Date): boolean => {
  const a = new Date(first);
  const b = new Date(second);

  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);

  return a.getTime() === b.getTime();
};

/**
 * Checks if two given dates are in the same year.
 * @param first - The first date to compare.
 * @param second - The second date to compare.
 * @returns Returns `true` if the given dates are in the same year, otherwise `false`.
 */
export const datesAreOnSameYear = (first: Date, second: Date): boolean => {
  const a = new Date(new Date(first).getFullYear(), 0, 1);
  const b = new Date(new Date(second).getFullYear(), 0, 1);
  return a.getTime() === b.getTime();
};

/**
 * Checks if a given date is in the current year.
 * @param date - The date to check.
 * @returns True if the given date is in the current year, false otherwise.
 */
export const dateIsInCurrentYear = (date: Date): boolean =>
  date ? new Date(date).getFullYear() === new Date().getFullYear() : false;

/**
 * Calculates the number of nights between two given dates.
 * @param startDate - The start date.
 * @param endDate - The end date.
 * @returns The number of nights between the given startDate and endDate.
 */
export const countNightsBetweenTwoDates = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const differenceInMilliseconds = Math.abs(end.getTime() - start.getTime());
  const differenceInDays = Math.ceil(differenceInMilliseconds / (1000 * 3600 * 24));

  return differenceInDays;
};

/**
 * Calculates the number of days between two given dates.
 * @param startDate - The start date.
 * @param endDate - The end date.
 * @returns The number of days between the given startDate and endDate.
 */
export const countDaysBetweenTwoDates = (startDate: Date, endDate: Date): number =>
  countNightsBetweenTwoDates(startDate, endDate) + 1;

/**
 * Calculates the number of hours between two given dates.
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns The number of hours between the given startDate and endDate.
 */
export const countHoursBetweenTwoDates = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setSeconds(0, 0);
  end.setSeconds(0, 0);

  const differenceInMilliseconds = Math.abs(end.getTime() - start.getTime());
  const hours = Math.ceil(differenceInMilliseconds / (1000 * 3600));
  return hours;
};

export type SeasonDates = {
  [SeasonEnum.Winter]: DateRange<Date>;
  [SeasonEnum.Spring]: DateRange<Date>;
  [SeasonEnum.Summer]: DateRange<Date>;
  [SeasonEnum.Autumn]: DateRange<Date>;
};
export const nextWinter = new DateRange(
  new Date(new Date().getFullYear(), 11, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear() + 1, 2, 20, 23, 59, 59, 999),
);
export const winter = new DateRange(
  new Date(new Date().getFullYear() - 1, 11, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear(), 2, 20, 23, 59, 59, 999),
);
export const nextSpring = new DateRange(
  new Date(new Date().getFullYear() + 1, 2, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear() + 1, 5, 20, 23, 59, 59, 999),
);
export const spring = new DateRange(
  new Date(new Date().getFullYear(), 2, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear(), 5, 20, 23, 59, 59, 999),
);
export const nextSummer = new DateRange(
  new Date(new Date().getFullYear() + 1, 5, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear() + 1, 8, 22, 23, 59, 59, 999),
);
export const summer = new DateRange(
  new Date(new Date().getFullYear(), 5, 21, 0, 0, 0, 0),
  new Date(new Date().getFullYear(), 8, 22, 23, 59, 59, 999),
);
export const nextAutumn = new DateRange(
  new Date(new Date().getFullYear() + 1, 8, 23, 0, 0, 0, 0),
  new Date(new Date().getFullYear() + 1, 11, 20, 23, 59, 59, 999),
);
export const autumn = new DateRange(
  new Date(new Date().getFullYear(), 8, 23, 0, 0, 0, 0),
  new Date(new Date().getFullYear(), 11, 20, 23, 59, 59, 999),
);
export const seasonChangeDates: {
  [key in SeasonEnum]: {
    startMonth: number;
    startDay: number;
    endMonth: number;
    endDay: number;
  };
} = {
  spring: { startMonth: 3, startDay: 21, endMonth: 6, endDay: 20 },
  summer: { startMonth: 6, startDay: 21, endMonth: 9, endDay: 22 },
  autumn: { startMonth: 9, startDay: 23, endMonth: 12, endDay: 20 },
  winter: { startMonth: 12, startDay: 21, endMonth: 3, endDay: 20 },
};

/**
 * Returns the current season based on the given date.
 *
 * @param {Date} date - The date to determine the season for.
 * @returns {SeasonEnum} - The current season.
 */
export const getSeason = (date: Date): SeasonEnum => {
  const month = date.getMonth() + 1; // Months are zero-based, so we add 1
  const day = date.getDate();

  if (
    (month === seasonChangeDates.spring.startMonth && day >= seasonChangeDates.spring.startDay) ||
    (month > seasonChangeDates.spring.startMonth && month < seasonChangeDates.spring.endMonth) ||
    (month === seasonChangeDates.spring.endMonth && day < seasonChangeDates.spring.endDay)
  ) {
    return SeasonEnum.Spring;
  } else if (
    (month === seasonChangeDates.summer.startMonth && day >= seasonChangeDates.summer.startDay) ||
    (month > seasonChangeDates.summer.startMonth && month < seasonChangeDates.summer.endMonth) ||
    (month === seasonChangeDates.summer.endMonth && day < seasonChangeDates.summer.endDay)
  ) {
    return SeasonEnum.Summer;
  } else if (
    (month === seasonChangeDates.autumn.startMonth && day >= seasonChangeDates.autumn.startDay) ||
    (month > seasonChangeDates.autumn.startMonth && month < seasonChangeDates.autumn.endMonth) ||
    (month === seasonChangeDates.autumn.endMonth && day < seasonChangeDates.autumn.endDay)
  ) {
    return SeasonEnum.Autumn;
  } else {
    return SeasonEnum.Winter;
  }
};

/**
 * Returns the current season based on the current date.
 *
 * @returns {SeasonEnum} The current season.
 */
export const getCurrentSeason = (): SeasonEnum => getSeason(new Date());

/**
 * Determines if the current date falls within the specified season.
 *
 * @param season - The season to check.
 * @returns true if the current date is within the specified season, false otherwise.
 */
export const isCurrentSeason = (season: SeasonEnum): boolean => {
  const month = today.getMonth() + 1; // Months are zero-based, so we add 1
  const day = today.getDate();

  const seasonLookup = {
    [SeasonEnum.Spring]: (month: number, day: number) =>
      (month === seasonChangeDates.spring.startMonth && day >= seasonChangeDates.spring.startDay) ||
      (month > seasonChangeDates.spring.startMonth && month < seasonChangeDates.spring.endMonth) ||
      (month === seasonChangeDates.spring.endMonth && day < seasonChangeDates.spring.endDay),
    [SeasonEnum.Summer]: (month: number, day: number) =>
      (month === seasonChangeDates.summer.startMonth && day >= seasonChangeDates.summer.startDay) ||
      (month > seasonChangeDates.summer.startMonth && month < seasonChangeDates.summer.endMonth) ||
      (month === seasonChangeDates.summer.endMonth && day < seasonChangeDates.summer.endDay),
    [SeasonEnum.Autumn]: (month: number, day: number) =>
      (month === seasonChangeDates.autumn.startMonth && day >= seasonChangeDates.autumn.startDay) ||
      (month > seasonChangeDates.autumn.startMonth && month < seasonChangeDates.autumn.endMonth) ||
      (month === seasonChangeDates.autumn.endMonth && day < seasonChangeDates.autumn.endDay),
    [SeasonEnum.Winter]: (month: number, day: number) =>
      (month === seasonChangeDates.winter.startMonth && day >= seasonChangeDates.winter.startDay) ||
      month < seasonChangeDates.winter.endMonth ||
      (month === seasonChangeDates.winter.endMonth && day < seasonChangeDates.winter.endDay),
  };

  return seasonLookup[season](month, day);
};
export const seasonDateRanges: SeasonDates = {
  [SeasonEnum.Spring]:
    today.getTime() <= spring.end.getTime() || isCurrentSeason(SeasonEnum.Spring) ? spring : nextSpring,
  [SeasonEnum.Summer]:
    today.getTime() <= summer.end.getTime() || isCurrentSeason(SeasonEnum.Summer) ? summer : nextSummer,
  [SeasonEnum.Autumn]:
    today.getTime() <= autumn.end.getTime() || isCurrentSeason(SeasonEnum.Autumn) ? autumn : nextAutumn,
  [SeasonEnum.Winter]:
    today.getTime() <= winter.end.getTime() || isCurrentSeason(SeasonEnum.Winter) ? winter : nextWinter,
};

/**
 * Generates a seasonDateRanges object based on the current season.
 * The object contains date ranges for each season (Spring, Summer, Autumn, Winter).
 * If the current season matches the specified season, the date range for that season is used.
 * Otherwise, the date range for the next season is used.
 *
 * @returns {SeasonDates} - The seasonDateRanges object with date ranges for each season.
 */
export const getSeasonDateRange = (season: SeasonEnum) => seasonDateRanges[season];

/**
 * This function takes a date in various formats (string, Date object, or an object with year, month, and day properties)
 * and converts it into a valid Date .
 * */
export const convertToDate = (date: any): Date => {
  if (date instanceof Date) {
    return date;
  } else if (typeof date === 'string') {
    return new Date(date);
  } else if (date.year && date.month && date.day) {
    return new Date(date.year, date.month - 1, date.day);
  }
  return null;
};

/**
 * This function checks if a given Date object is present in an array of dates to be excluded.
 * It compares the year, month, and day values to determine if the date matches any of the excluded dates.
 * */
export const isDateExcluded = (date: Date, dataToEsclude: any[]): boolean => {
  const excludedDates: Date[] = dataToEsclude.map((date: string) => convertToDate(date)).filter(date => date !== null);
  return excludedDates.some(
    excludedDate =>
      excludedDate.getDate() === date.getDate() &&
      excludedDate.getMonth() === date.getMonth() &&
      excludedDate.getFullYear() === date.getFullYear(),
  );
};
